import type { StudioProduct } from '../../supabase/functions/wersee-ai/studioContract';
import { DatabaseService } from './databaseService';
import { uploadWerseeFile } from './werseeStorage';
import { privateListingObjectReference, saveListingContentManifest } from './listingContentAccess';
import { validateStudioPdf } from '../lib/studioArtifactValidation';

export type StudioAssetProgress = {
  id: string;
  coverUrl?: string;
  bannerUrl?: string;
  thumbnailUrls?: string[];
  pdfReference?: string;
  draftSaved?: boolean;
  complete?: boolean;
  pageCount?: number;
};
export async function referenceImageData(file: File) {
  const bitmap = await createImageBitmap(file);
  try {
    const scale = Math.min(1, 1200 / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement('canvas'); canvas.width = Math.round(bitmap.width * scale); canvas.height = Math.round(bitmap.height * scale);
    const ctx = canvas.getContext('2d'); if (!ctx) throw new Error('Image processing is unavailable.');
    ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, canvas.width, canvas.height); ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL('image/jpeg', .76);
  } finally { bitmap.close(); }
}

/** A labelled contact sheet preserves all references in one vision request. */
export async function referenceImagesData(files: File[]) {
  if (files.length <= 1) return Promise.all(files.map(referenceImageData));
  const images = await Promise.all(files.slice(0, 4).map(file => createImageBitmap(file)));
  try {
    const canvas = document.createElement('canvas'); canvas.width = 1200; canvas.height = images.length > 2 ? 1200 : 600;
    const ctx = canvas.getContext('2d'); if (!ctx) throw new Error('Image processing is unavailable.');
    ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, canvas.width, canvas.height);
    images.forEach((bitmap, index) => {
      const x = (index % 2) * 600, y = Math.floor(index / 2) * 600;
      const scale = Math.min(560 / bitmap.width, 530 / bitmap.height);
      const width = bitmap.width * scale, height = bitmap.height * scale;
      ctx.drawImage(bitmap, x + (600 - width) / 2, y + 38 + (550 - height) / 2, width, height);
      ctx.fillStyle = '#333333'; ctx.font = '20px sans-serif'; ctx.fillText(`Reference ${index + 1}`, x + 20, y + 28);
    });
    return [canvas.toDataURL('image/jpeg', .76)];
  } finally { images.forEach(bitmap => bitmap.close()); }
}

function hexToRgb(hex: string) {
  const clean = hex.replace('#', '');
  const r = parseInt(clean.substring(0, 2), 16) || 40;
  const g = parseInt(clean.substring(2, 4), 16) || 40;
  const b = parseInt(clean.substring(4, 6), 16) || 40;
  return { r, g, b, isLight: r * 0.299 + g * 0.587 + b * 0.114 > 150 };
}

function canvasBlob(canvas: HTMLCanvasElement, type = 'image/png', quality = 0.92) {
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(blob => blob ? resolve(blob) : reject(new Error('Canvas export failed')), type, quality);
  });
}

function wrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number) {
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let current = '';
  for (const word of words) {
    const test = current ? `${current} ${word}` : word;
    if (ctx.measureText(test).width > maxWidth && current) {
      lines.push(current);
      current = word;
    } else {
      current = test;
    }
  }
  if (current) lines.push(current);
  return lines;
}

/** Generates rich storefront thumbnails: 1 background banner (16:9), 1 main cover (4:5), and 3 inside page previews (total 5 thumbnails) */
export async function generateStudioThumbnails(product: StudioProduct, slug: string) {
  // 1. Background Banner Thumbnail (1920x1080 - 16:9 for storefront background & hero banner)
  const bannerCanvas = document.createElement('canvas');
  bannerCanvas.width = 1920; bannerCanvas.height = 1080;
  const bCtx = bannerCanvas.getContext('2d')!;
  const bannerGrad = bCtx.createLinearGradient(0, 0, 1920, 1080);
  bannerGrad.addColorStop(0, '#0d0d12');
  bannerGrad.addColorStop(0.5, product.color);
  bannerGrad.addColorStop(1, '#09090c');
  bCtx.fillStyle = bannerGrad;
  bCtx.fillRect(0, 0, 1920, 1080);

  // Ambient glows and grid lines
  bCtx.fillStyle = product.accent;
  bCtx.globalAlpha = 0.25;
  bCtx.beginPath();
  bCtx.arc(1480, 480, 520, 0, Math.PI * 2);
  bCtx.fill();
  bCtx.beginPath();
  bCtx.arc(420, 920, 400, 0, Math.PI * 2);
  bCtx.fill();
  bCtx.globalAlpha = 1;

  bCtx.strokeStyle = 'rgba(255, 255, 255, 0.08)';
  bCtx.lineWidth = 1;
  for (let x = 120; x < 1920; x += 180) {
    bCtx.beginPath(); bCtx.moveTo(x, 0); bCtx.lineTo(x, 1080); bCtx.stroke();
  }
  for (let y = 100; y < 1080; y += 180) {
    bCtx.beginPath(); bCtx.moveTo(0, y); bCtx.lineTo(1920, y); bCtx.stroke();
  }

  // Left banner copy
  bCtx.fillStyle = 'rgba(255, 255, 255, 0.15)';
  bCtx.fillRect(140, 180, 320, 48);
  bCtx.fillStyle = '#ffffff';
  bCtx.font = '600 16px sans-serif';
  bCtx.fillText('★ OFFICIAL WERSEE RELEASE', 165, 210);

  bCtx.font = '700 74px sans-serif';
  const bannerTitleLines = wrapText(bCtx, product.title, 940).slice(0, 3);
  bannerTitleLines.forEach((line, i) => bCtx.fillText(line, 140, 330 + i * 86));

  bCtx.font = '400 28px sans-serif';
  bCtx.fillStyle = 'rgba(255, 255, 255, 0.78)';
  const bannerDescLines = wrapText(bCtx, product.shortDescription, 880).slice(0, 2);
  bannerDescLines.forEach((line, i) => bCtx.fillText(line, 140, 600 + i * 42));

  // Feature badges on banner
  bCtx.fillStyle = 'rgba(255, 255, 255, 0.1)';
  bCtx.fillRect(140, 720, 260, 56);
  bCtx.fillRect(430, 720, 260, 56);
  bCtx.fillStyle = '#ffffff';
  bCtx.font = '600 20px sans-serif';
  bCtx.fillText(`${product.format.toUpperCase()} · PDF`, 175, 755);
  bCtx.fillText(`€${product.price} · DIRECT TOEGANG`, 455, 755);

  // Right side 3D card preview
  bCtx.save();
  bCtx.translate(1420, 520);
  bCtx.rotate(0.08);
  bCtx.shadowColor = 'rgba(0, 0, 0, 0.45)';
  bCtx.shadowBlur = 50;
  bCtx.shadowOffsetY = 30;
  bCtx.fillStyle = product.color;
  bCtx.fillRect(-260, -360, 520, 720);
  bCtx.fillStyle = product.accent;
  bCtx.fillRect(-230, -330, 460, 360);
  bCtx.fillStyle = '#ffffff';
  bCtx.font = '700 38px sans-serif';
  const mockupLines = wrapText(bCtx, product.title, 420).slice(0, 2);
  mockupLines.forEach((l, i) => bCtx.fillText(l, -210, 110 + i * 48));
  bCtx.font = '500 22px sans-serif';
  bCtx.fillStyle = 'rgba(255, 255, 255, 0.8)';
  bCtx.fillText(`Inclusief ${product.sections.length} Hoofdstukken`, -210, 240);
  bCtx.restore();

  // 2. Primary Product Cover Thumbnail (1200x1500 - 4:5 portrait)
  const coverCanvas = document.createElement('canvas');
  coverCanvas.width = 1200; coverCanvas.height = 1500;
  const cCtx = coverCanvas.getContext('2d')!;
  const coverGrad = cCtx.createLinearGradient(0, 0, 1200, 1500);
  coverGrad.addColorStop(0, product.color);
  coverGrad.addColorStop(1, '#111118');
  cCtx.fillStyle = coverGrad;
  cCtx.fillRect(0, 0, 1200, 1500);

  cCtx.fillStyle = product.accent;
  cCtx.globalAlpha = 0.55;
  cCtx.beginPath();
  cCtx.arc(1040, 260, 540, 0, Math.PI * 2);
  cCtx.fill();
  cCtx.globalAlpha = 1;

  cCtx.fillStyle = 'rgba(255, 255, 255, 0.16)';
  cCtx.fillRect(100, 110, 380, 52);
  cCtx.fillStyle = '#ffffff';
  cCtx.font = '600 20px sans-serif';
  cCtx.fillText(`WERSEE · ${product.category.toUpperCase()}`, 130, 143);

  cCtx.fillStyle = 'rgba(255, 255, 255, 0.2)';
  cCtx.fillRect(100, 200, 1000, 480);
  cCtx.fillStyle = '#ffffff';
  cCtx.font = '700 84px sans-serif';
  const coverLines = wrapText(cCtx, product.title, 900).slice(0, 4);
  coverLines.forEach((l, i) => cCtx.fillText(l, 140, 340 + i * 94));

  cCtx.font = '400 32px sans-serif';
  cCtx.fillStyle = 'rgba(255, 255, 255, 0.82)';
  const coverDesc = wrapText(cCtx, product.shortDescription, 940).slice(0, 3);
  coverDesc.forEach((l, i) => cCtx.fillText(l, 105, 820 + i * 48));

  product.features.slice(0, 3).forEach((feat, idx) => {
    cCtx.fillStyle = 'rgba(255, 255, 255, 0.08)';
    cCtx.fillRect(100, 1040 + idx * 80, 1000, 62);
    cCtx.fillStyle = '#ffffff';
    cCtx.font = '600 24px sans-serif';
    cCtx.fillText(`✓  ${feat}`, 140, 1080 + idx * 80);
  });

  cCtx.fillStyle = 'rgba(0, 0, 0, 0.35)';
  cCtx.fillRect(0, 1340, 1200, 160);
  cCtx.fillStyle = '#ffffff';
  cCtx.font = '700 36px sans-serif';
  cCtx.fillText(`€${product.price}`, 100, 1435);
  cCtx.font = '500 22px sans-serif';
  cCtx.fillStyle = 'rgba(255, 255, 255, 0.65)';
  cCtx.fillText(`${product.format.toUpperCase()} · DIRECT DOWNLOAD · PRINTBAAR`, 260, 1432);

  // 3. Storefront Preview 1: Table of Contents & Structure (1200x1500)
  const tocCanvas = document.createElement('canvas');
  tocCanvas.width = 1200; tocCanvas.height = 1500;
  const tCtx = tocCanvas.getContext('2d')!;
  tCtx.fillStyle = '#14141c';
  tCtx.fillRect(0, 0, 1200, 1500);

  tCtx.fillStyle = product.color;
  tCtx.globalAlpha = 0.2;
  tCtx.fillRect(0, 0, 1200, 260);
  tCtx.globalAlpha = 1;

  tCtx.fillStyle = product.accent;
  tCtx.font = '700 20px sans-serif';
  tCtx.fillText('STOREFRONT PREVIEW · INHOUD & OPBOUW', 100, 95);
  tCtx.fillStyle = '#ffffff';
  tCtx.font = '700 52px sans-serif';
  tCtx.fillText('Inhoudsopgave & Modulestructuur', 100, 170);

  product.sections.slice(0, 5).forEach((sec, idx) => {
    const y = 320 + idx * 195;
    tCtx.fillStyle = 'rgba(255, 255, 255, 0.05)';
    tCtx.fillRect(100, y, 1000, 165);
    tCtx.fillStyle = product.accent;
    tCtx.font = '700 28px sans-serif';
    tCtx.fillText(`0${idx + 1}`, 140, y + 55);
    tCtx.fillStyle = '#ffffff';
    tCtx.font = '600 32px sans-serif';
    tCtx.fillText(sec.title, 210, y + 55);
    tCtx.font = '400 22px sans-serif';
    tCtx.fillStyle = 'rgba(255, 255, 255, 0.65)';
    const snippet = sec.body.replace(/\s+/g, ' ').slice(0, 140) + '…';
    const snipLines = wrapText(tCtx, snippet, 880).slice(0, 2);
    snipLines.forEach((sl, si) => tCtx.fillText(sl, 210, y + 100 + si * 32));
  });

  tCtx.fillStyle = 'rgba(255, 255, 255, 0.08)';
  tCtx.fillRect(100, 1340, 1000, 75);
  tCtx.fillStyle = '#ffffff';
  tCtx.font = '600 22px sans-serif';
  tCtx.fillText(`★ Volledige digitale gids · ${product.sections.length} Modules · Direct bewerkbaar`, 150, 1385);

  // 4. Storefront Preview 2: Interactive Worksheets & Exercises (1200x1500)
  const workCanvas = document.createElement('canvas');
  workCanvas.width = 1200; workCanvas.height = 1500;
  const wCtx = workCanvas.getContext('2d')!;
  wCtx.fillStyle = '#101017';
  wCtx.fillRect(0, 0, 1200, 1500);

  wCtx.fillStyle = product.accent;
  wCtx.font = '700 20px sans-serif';
  wCtx.fillText('STOREFRONT PREVIEW · WERKBLADEN & ACTIESTAPPEN', 100, 95);
  wCtx.fillStyle = '#ffffff';
  wCtx.font = '700 52px sans-serif';
  wCtx.fillText('Interactieve Werkbladen & Checklists', 100, 170);

  wCtx.fillStyle = 'rgba(255, 255, 255, 0.06)';
  wCtx.fillRect(100, 260, 1000, 320);
  wCtx.fillStyle = '#ffffff';
  wCtx.font = '700 30px sans-serif';
  wCtx.fillText('Prioriteiten & Doelen Matrix', 140, 320);
  wCtx.font = '400 22px sans-serif';
  wCtx.fillStyle = 'rgba(255, 255, 255, 0.7)';
  wCtx.fillText('✓ Wekelijkse en dagelijkse focuspunten', 140, 375);
  wCtx.fillText('✓ Urgent vs. Belangrijk quadrant analyse', 140, 420);
  wCtx.fillText('✓ Gewoonte tracker met 7-dagen cyclus', 140, 465);

  wCtx.fillStyle = 'rgba(255, 255, 255, 0.06)';
  wCtx.fillRect(100, 620, 1000, 320);
  wCtx.fillStyle = '#ffffff';
  wCtx.font = '700 30px sans-serif';
  wCtx.fillText('Stap-voor-stap Implementatie Blueprint', 140, 680);
  wCtx.font = '400 22px sans-serif';
  wCtx.fillStyle = 'rgba(255, 255, 255, 0.7)';
  wCtx.fillText('✓ Direct in te vullen invulvelden & reflectievragen', 140, 735);
  wCtx.fillText('✓ Meetbare KPI targets en actiepunten', 140, 780);
  wCtx.fillText('✓ Foutmarge controle en kwaliteitscheck', 140, 825);

  wCtx.fillStyle = 'rgba(255, 255, 255, 0.06)';
  wCtx.fillRect(100, 980, 1000, 320);
  wCtx.fillStyle = '#ffffff';
  wCtx.font = '700 30px sans-serif';
  wCtx.fillText('Snelle Audit & Opleveringslijst', 140, 1040);
  wCtx.font = '400 22px sans-serif';
  wCtx.fillStyle = 'rgba(255, 255, 255, 0.7)';
  wCtx.fillText('✓ Eindcontrole voor launch of toepassing', 140, 1095);
  wCtx.fillText('✓ Handige checklists die je kunt afvinken', 140, 1140);
  wCtx.fillText('✓ Direct printbaar op A4 formaat', 140, 1185);

  wCtx.fillStyle = product.color;
  wCtx.fillRect(100, 1340, 1000, 75);
  wCtx.fillStyle = '#ffffff';
  wCtx.font = '600 22px sans-serif';
  wCtx.fillText('★ Ontworpen om direct in de praktijk te brengen · Geen theorie zonder actie', 140, 1385);

  // 5. Storefront Preview 3: Features & Specificaties (1200x1500)
  const featCanvas = document.createElement('canvas');
  featCanvas.width = 1200; featCanvas.height = 1500;
  const fCtx = featCanvas.getContext('2d')!;
  fCtx.fillStyle = '#111119';
  fCtx.fillRect(0, 0, 1200, 1500);

  fCtx.fillStyle = product.accent;
  fCtx.font = '700 20px sans-serif';
  fCtx.fillText('STOREFRONT PREVIEW · OVERZICHT & HIGHLIGHTS', 100, 95);
  fCtx.fillStyle = '#ffffff';
  fCtx.font = '700 52px sans-serif';
  fCtx.fillText('Wat je krijgt in dit pakket', 100, 170);

  product.features.slice(0, 6).forEach((feat, idx) => {
    const y = 260 + idx * 165;
    fCtx.fillStyle = 'rgba(255, 255, 255, 0.05)';
    fCtx.fillRect(100, y, 1000, 135);
    fCtx.fillStyle = product.accent;
    fCtx.fillRect(100, y, 12, 135);
    fCtx.fillStyle = '#ffffff';
    fCtx.font = '700 28px sans-serif';
    fCtx.fillText(`Feature 0${idx + 1}`, 150, y + 50);
    fCtx.font = '400 24px sans-serif';
    fCtx.fillStyle = 'rgba(255, 255, 255, 0.8)';
    fCtx.fillText(feat, 150, y + 95);
  });

  fCtx.fillStyle = 'rgba(255, 255, 255, 0.08)';
  fCtx.fillRect(100, 1310, 1000, 120);
  fCtx.fillStyle = '#ffffff';
  fCtx.font = '700 28px sans-serif';
  fCtx.fillText(`Prijs: €${product.price} · Eigendom voor altijd`, 150, 1360);
  fCtx.font = '400 20px sans-serif';
  fCtx.fillStyle = 'rgba(255, 255, 255, 0.65)';
  fCtx.fillText('Direct downloaden in je Wersee Workspace & beveiligd bewaard', 150, 1400);

  const [bannerBlob, coverBlob, tocBlob, workBlob, featBlob] = await Promise.all([
    canvasBlob(bannerCanvas, 'image/jpeg', 0.9),
    canvasBlob(coverCanvas, 'image/png'),
    canvasBlob(tocCanvas, 'image/jpeg', 0.88),
    canvasBlob(workCanvas, 'image/jpeg', 0.88),
    canvasBlob(featCanvas, 'image/jpeg', 0.88),
  ]);

  return {
    banner: new File([bannerBlob], `${slug}-banner.jpg`, { type: 'image/jpeg' }),
    cover: new File([coverBlob], `${slug}-cover.png`, { type: 'image/png' }),
    previews: [
      new File([tocBlob], `${slug}-preview-toc.jpg`, { type: 'image/jpeg' }),
      new File([workBlob], `${slug}-preview-worksheets.jpg`, { type: 'image/jpeg' }),
      new File([featBlob], `${slug}-preview-features.jpg`, { type: 'image/jpeg' }),
    ],
    coverDataUrl: coverCanvas.toDataURL('image/jpeg', 0.88),
  };
}

export async function makeStudioFiles(product: StudioProduct) {
  const { jsPDF } = await import('jspdf');
  const slug = product.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 60) || 'digital-product';
  const thumbnails = await generateStudioThumbnails(product, slug);

  const doc = new jsPDF({ compress: true });
  const rgb = hexToRgb(product.color);
  const accentRgb = hexToRgb(product.accent);

  // Page 1: Full Cover Page
  doc.setFillColor(product.color);
  doc.rect(0, 0, 210, 297, 'F');
  doc.addImage(thumbnails.coverDataUrl, 'JPEG', 0, 0, 210, 262.5);

  let y = 30;
  const newPage = (withHeader = true) => {
    doc.addPage();
    y = 35;
    if (withHeader) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(140, 140, 150);
      doc.text(`${product.title.toUpperCase()} · ${product.format.toUpperCase()}`, 22, 18);
      doc.setDrawColor(230, 230, 235);
      doc.line(22, 22, 188, 22);
    }
  };

  const write = (text: string, size: number, bold = false, color: [number, number, number] = [35, 35, 42]) => {
    doc.setFont('helvetica', bold ? 'bold' : 'normal');
    doc.setFontSize(size);
    doc.setTextColor(color[0], color[1], color[2]);
    const printable = text
      .replace(/[\u2010-\u2015\u2212]/g, '-')
      .replace(/[\u2018\u2019]/g, "'")
      .replace(/[\u201c\u201d]/g, '"')
      .replace(/\u00a0/g, ' ');
    const lines = doc.splitTextToSize(printable, 166) as string[];
    for (const line of lines) {
      if (y > 270) newPage();
      doc.text(line, 22, y);
      y += size * 0.48;
    }
    y += 7;
  };

  // Page 2: Executive Summary & Table of Contents
  newPage();
  doc.setFillColor(rgb.r, rgb.g, rgb.b);
  doc.rect(22, y - 4, 166, 18, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.setTextColor(rgb.isLight ? 30 : 255);
  doc.text('INHOUD & OVERZICHT', 27, y + 8);
  y += 28;

  write(product.title, 24, true);
  write(product.shortDescription, 12, false, [90, 90, 100]);
  y += 6;

  doc.setFillColor(248, 248, 252);
  doc.roundedRect(22, y, 166, 32, 3, 3, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(40, 40, 50);
  doc.text('Welkom & Handleiding', 28, y + 12);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(90, 90, 105);
  doc.text('Dit document is ontworpen om je direct praktische houvast en meetbare resultaten te bieden.', 28, y + 20);
  doc.text('Volg de modules in volgorde of gebruik de praktische werkbladen direct in je werk.', 28, y + 26);
  y += 44;

  write('Inhoudsopgave & Modulestructuur', 16, true);
  product.sections.forEach((section, idx) => {
    if (y > 260) newPage();
    doc.setFillColor(243, 244, 248);
    doc.roundedRect(22, y - 4, 166, 14, 2, 2, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(45, 45, 55);
    doc.text(`Module 0${idx + 1}: ${section.title}`, 28, y + 5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(130, 130, 145);
    doc.text(`Pagina ${idx + 3}`, 168, y + 5);
    y += 18;
  });

  // Pages 3+: In-depth Chapter & Module Content Pages
  for (let idx = 0; idx < product.sections.length; idx++) {
    const section = product.sections[idx];
    newPage();

    doc.setFillColor(242, 243, 248);
    doc.roundedRect(22, y - 4, 60, 12, 2, 2, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(rgb.r, rgb.g, rgb.b);
    doc.text(`MODULE 0${idx + 1}`, 26, y + 4);
    y += 18;

    write(section.title, 20, true);
    y += 2;

    write(section.body, 11, false, [45, 45, 52]);

    if (y > 220) newPage();
    doc.setFillColor(248, 249, 253);
    doc.setDrawColor(accentRgb.r, accentRgb.g, accentRgb.b);
    doc.roundedRect(22, y, 166, 26, 2, 2, 'FD');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(accentRgb.r, accentRgb.g, accentRgb.b);
    doc.text('★ Belangrijk Inzicht & Toepassing', 28, y + 10);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(70, 70, 80);
    doc.text('Pas deze principes direct toe in kleine stappen voor maximaal resultaat en overzicht.', 28, y + 18);
    y += 34;

    if (y > 230) newPage();
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(40, 40, 50);
    doc.text('Actiestappen voor deze module:', 22, y);
    y += 8;
    ['Eerste actiepunt vastleggen en inplannen', 'Kerninzichten bespreken of documenteren', 'Resultaten toetsen aan je doelstellingen'].forEach(step => {
      doc.setDrawColor(180, 180, 190);
      doc.rect(23, y - 3, 3.5, 3.5);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(75, 75, 85);
      doc.text(step, 30, y);
      y += 8;
    });
    y += 6;
  }

  const isDutch = /\b(je|een|voor|met|het)\b/i.test(product.sections.map(s => s.body).join(' '));
  if (product.format === 'planner') {
    newPage();
    write(isDutch ? 'Wekelijkse Prioriteiten & Doelen' : 'Weekly Priorities & Goals', 20, true);
    doc.setDrawColor(210);
    doc.setFontSize(10);
    const dayWidth = 166 / 7;
    const days = isDutch ? ['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo'] : ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    days.forEach((day, index) => {
      const x = 22 + index * dayWidth;
      doc.rect(x, dayWidth, 120, 120);
      doc.text(day, x + 3, y + 8);
      for (let row = 1; row < 7; row++) doc.line(x, y + 12 + row * 16, x + dayWidth, y + 12 + row * 16);
    });
    y += 132;
    write(isDutch ? 'Top 3 Doelen voor deze week' : 'Top 3 Goals This Week', 13, true);
    for (let row = 0; row < 3; row++) {
      doc.rect(22, y - 3, 4, 4);
      doc.line(30, y, 188, y);
      y += 13;
    }

    newPage();
    write(isDutch ? 'Dagelijkse Focus & Tijdsblokken' : 'Daily Deep Work & Timeblocks', 20, true);
    for (let b = 0; b < 6; b++) {
      doc.setFillColor(248, 249, 252);
      doc.roundedRect(22, y, 166, 24, 2, 2, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(60, 60, 70);
      doc.text(`Tijdsblok 0${b + 1}  (Focus Sessie)`, 28, y + 9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(140, 140, 150);
      doc.text('Doel / Resultaat: ____________________________________________________', 28, y + 18);
      y += 32;
    }
  } else if (['workbook', 'templates', 'guide'].includes(product.format)) {
    newPage();
    write(isDutch ? 'Reflectie & Strategisch Actieplan' : 'Reflection & Implementation Worksheet', 20, true);
    write(isDutch ? 'Beantwoord de onderstaande kernvragen om je strategie scherp te stellen:' : 'Answer the guided questions below to execute with confidence:', 11);
    const questions = isDutch
      ? ['Wat is het grootste obstakel dat je momenteel belemmert?', 'Welke 3 concrete acties leveren binnen 7 dagen het meeste effect op?', 'Welke hulpmiddelen of gewoontes heb je nodig om dit vol te houden?']
      : ['What is the main obstacle currently holding you back?', 'Which 3 concrete actions will generate the biggest momentum within 7 days?', 'What resources or habits are needed to maintain consistency?'];
    questions.forEach((q, qi) => {
      if (y > 240) newPage();
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(40, 40, 50);
      doc.text(`${qi + 1}. ${q}`, 22, y);
      y += 8;
      doc.setDrawColor(215);
      for (let l = 0; l < 4; l++) {
        doc.line(22, y, 188, y);
        y += 12;
      }
      y += 6;
    });
  } else if (product.format === 'checklist') {
    newPage();
    write(isDutch ? 'Uitgebreide Audit & Checklist' : 'Comprehensive Action Audit & Checklist', 20, true);
    doc.setDrawColor(210);
    for (const section of product.sections) {
      if (y > 230) newPage();
      doc.rect(22, y - 4, 4, 4);
      y += 7;
      write(section.title, 12, true);
      doc.line(22, y, 188, y);
      y += 11;
    }
  }

  // Final Page: Concluding Summary & Resource Directory
  newPage();
  doc.setFillColor(rgb.r, rgb.g, rgb.b);
  doc.rect(22, y - 4, 166, 16, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(rgb.isLight ? 30 : 255);
  doc.text(isDutch ? 'AFSLUITING & VOLGENDE STAPPEN' : 'CONCLUSION & NEXT STEPS', 27, y + 7);
  y += 26;

  write(isDutch ? 'Gefeliciteerd met je inzet!' : 'Congratulations on taking action!', 18, true);
  write(
    isDutch
      ? 'De waarde van dit document zit in de herhaling en concrete toepassing. Gebruik de checklists en sjablonen regelmatig om je workflow te verfijnen.'
      : 'The value of this publication lies in consistent execution. Return to the checklists and templates regularly to keep refining your workflow.',
    11,
  );
  y += 6;

  doc.setFillColor(248, 248, 252);
  doc.roundedRect(22, y, 166, 50, 3, 3, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(40, 40, 50);
  doc.text(isDutch ? 'Productinformatie' : 'Product information', 28, y + 12);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(110, 110, 120);
  doc.text(isDutch ? 'Gemaakt met Buddy Studio. Controleer de inhoud voor gebruik.' : 'Created with Buddy Studio. Review the content before use.', 28, y + 20);
  doc.text(`Formaat: ${product.format.toUpperCase()} · Categorie: ${product.category}`, 28, y + 26);
  doc.text(`Totale omvang: ${doc.getNumberOfPages()} pagina's digitaal materiaal`, 28, y + 32);
  doc.text('Alle rechten voorbehouden aan de auteur. Veilig bewaard in Wersee Cloud.', 28, y + 38);

  const totalPages = doc.getNumberOfPages();
  for (let page = 2; page <= totalPages; page++) {
    doc.setPage(page);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(140, 140, 150);
    doc.text(`Pagina ${page} van ${totalPages}`, 22, 288);
    doc.text('Wersee Digital Release', 158, 288);
  }

  const pdfBlob = doc.output('blob');
  await validateStudioPdf(pdfBlob, totalPages);
  // Previews are rasterizations of the actual downloadable PDF, rather than
  // independent illustrations of worksheets that might not be included.
  const { pdfjs } = await import('react-pdf');
  const { default: workerUrl } = await import('pdfjs-dist/build/pdf.worker.min.mjs?url');
  pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;
  const document = await pdfjs.getDocument({ data: new Uint8Array(await pdfBlob.arrayBuffer()) }).promise;
  const previews: File[] = [];
  try {
    for (const pageNumber of [2, 3, Math.min(4, document.numPages)]) {
      const page = await document.getPage(pageNumber);
      const viewport = page.getViewport({ scale: 1.4 });
      const canvas = window.document.createElement('canvas'); canvas.width = Math.ceil(viewport.width); canvas.height = Math.ceil(viewport.height);
      const context = canvas.getContext('2d'); if (!context) throw new Error('PDF-preview is niet beschikbaar.');
      await page.render({ canvas, canvasContext: context, viewport }).promise;
      previews.push(new File([await canvasBlob(canvas, 'image/jpeg', .88)], `${slug}-page-${pageNumber}.jpg`, { type: 'image/jpeg' }));
      page.cleanup();
    }
  } finally { await document.destroy(); }
  return {
    cover: thumbnails.cover,
    banner: thumbnails.banner,
    previews,
    pdf: new File([pdfBlob], `${slug}.pdf`, { type: 'application/pdf' }),
    pages: totalPages,
  };
}

/** Progress is retained across retries so an interrupted collection never inserts duplicate drafts. */
export async function saveStudioProduct(
  product: StudioProduct,
  userId: string,
  progress: StudioAssetProgress,
  update: (progress: StudioAssetProgress) => void,
  createFiles = makeStudioFiles,
  onStep?: (step: string) => void,
) {
  let current = { ...progress };
  const report = (patch: Partial<StudioAssetProgress>) => {
    current = { ...current, ...patch };
    update(current);
  };

  onStep?.('Ontwerpen van thumbnails en document');
  const files = await createFiles(product);
  const quality = await validateStudioPdf(files.pdf, files.pages);
  const uploadedThumbnails: string[] = current.thumbnailUrls ? [...current.thumbnailUrls] : [];

  // 1. Save primary cover
  if (!current.coverUrl) {
    onStep?.('Opslaan van hoofdomslag (4:5)');
    const cover = await uploadWerseeFile({
      bucketId: 'listings',
      logicalPath: `${userId}/studio/${current.id}/cover.png`,
      file: files.cover,
    });
    if (!cover.url || cover.visibility !== 'public') throw new Error('Product cover could not be stored.');
    report({ coverUrl: cover.url, thumbnailUrls: [cover.url] });
    if (!uploadedThumbnails.includes(cover.url)) uploadedThumbnails.push(cover.url);
  }

  // 2. Save background banner (16:9 for storefront background & hero)
  if (!current.bannerUrl && files.banner) {
    onStep?.('Opslaan van storefront achtergrondbanner (16:9)');
      const banner = await uploadWerseeFile({
        bucketId: 'listings',
        logicalPath: `${userId}/studio/${current.id}/banner.jpg`,
        file: files.banner,
      });
      if (banner.url && banner.visibility === 'public') {
        if (!uploadedThumbnails.includes(banner.url)) uploadedThumbnails.push(banner.url);
        report({ bannerUrl: banner.url, thumbnailUrls: [...uploadedThumbnails] });
      } else throw new Error('De banner kon niet publiek worden opgeslagen.');
  }

  // 3. Save inside previews (3 storefront previews)
  if (files.previews && files.previews.length) {
    onStep?.('Opslaan van storefront pagina previews');
    for (let pIdx = 0; pIdx < files.previews.length; pIdx++) {
      if (uploadedThumbnails.length >= pIdx + 3) continue;
        const preview = await uploadWerseeFile({
          bucketId: 'listings',
          logicalPath: `${userId}/studio/${current.id}/preview-${pIdx + 1}.jpg`,
          file: files.previews[pIdx],
        });
        if (preview.url && preview.visibility === 'public' && !uploadedThumbnails.includes(preview.url)) {
          uploadedThumbnails.push(preview.url);
          report({ thumbnailUrls: [...uploadedThumbnails] });
        } else throw new Error('Een PDF-preview kon niet worden opgeslagen.');
    }
    report({ thumbnailUrls: uploadedThumbnails });
  }

  // 4. Save protected multi-page PDF
  if (!current.pdfReference) {
    onStep?.(`Opslaan van ${files.pages} pagina's digitaal product (PDF)`);
    const pdf = await uploadWerseeFile({
      bucketId: 'digital-downloads',
      logicalPath: `${userId}/studio/${current.id}/product.pdf`,
      file: files.pdf,
    });
    if (pdf.visibility !== 'private') throw new Error('The download must be stored privately.');
    report({ pdfReference: privateListingObjectReference(pdf.objectId), pageCount: files.pages });
  }

  // 5. Save listing draft with all thumbnails and layout settings
  if (!current.draftSaved) {
    onStep?.('Aanmaken van storefront product draft');
    const imagesToSave = uploadedThumbnails.length > 0 ? uploadedThumbnails : [current.coverUrl!];
    try {
      await DatabaseService.insert('listings', {
        id: current.id,
        user_id: userId,
        seller_id: userId,
        type: 'digital',
        status: 'draft',
        title: product.title,
        description: product.description,
        short_description: product.shortDescription,
        seo_title: product.listing?.seoTitle || product.title.slice(0, 70),
        seo_description: product.listing?.seoDescription || product.shortDescription.slice(0, 170),
        features: product.features,
        faqs: product.listing?.faqs || [],
        price: product.price,
        base_currency: product.currency,
        category: product.category,
        image_url: current.coverUrl || imagesToSave[0],
        images: imagesToSave,
        metadata: {
          currency: product.currency,
          digitalType: 'ebook',
          format: 'PDF',
          features: product.features,
          pages: files.pages,
          bannerUrl: current.bannerUrl,
          thumbnailCount: imagesToSave.length,
          studioGenerated: true,
          origin: 'wersee_ai',
          creation_mode: 'wersee_ai',
          ai_generated: true,
          ai_creator: 'wersee_ai',
          artifactQuality: quality,
          marketing: product.marketing,
          studioListing: product.listing,
          tags: product.listing?.tags || [],
        },
        layout_settings: {
          backgroundColor: product.color,
          bannerUrl: current.bannerUrl,
        },
      });
    } catch (error) {
      const existing = await DatabaseService.get('listings', { eq: { id: current.id, user_id: userId }, single: true }).catch(() => null);
      if (!existing) throw error;
    }
    report({ draftSaved: true });
  }

  onStep?.('Koppelen van downloads & storefront manifest');
  await saveListingContentManifest(current.id, 'digital', {
    title: product.title,
    description: product.description,
    shortDescription: product.shortDescription,
    language: product.listing?.language,
    softwareRequirements: product.listing?.softwareRequirements,
    usageInstructions: product.listing?.usageInstructions,
    digitalType: 'ebook',
    mainFileUrl: current.pdfReference,
    coverUrl: current.coverUrl,
    bannerUrl: current.bannerUrl,
    images: uploadedThumbnails,
    format: 'PDF',
    pages: String(files.pages),
    fileSize: String(files.pdf.size),
    features: product.features,
    category: product.category,
    price: String(product.price),
    studioFormat: product.format,
    origin: 'wersee_ai',
    creation_mode: 'wersee_ai',
    studioGenerated: 'true',
  });

  report({ complete: true, pageCount: files.pages });
  return current;
}
